import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const QuestionBankEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { group } = location.state || {};
  
  // Parse options and images if they're strings and ensure each question has proper structure
  const initialQuestions = (group?.questions || []).map(q => {
    let parsedOptions = q.options;
    let parsedImages = q.images;
    
    // Parse options
    if (typeof q.options === 'string') {
      try {
        parsedOptions = JSON.parse(q.options);
      } catch (e) {
        parsedOptions = [];
      }
    } else if (!Array.isArray(q.options)) {
      parsedOptions = [];
    }
    
    // Parse images
    if (typeof q.images === 'string') {
      try {
        parsedImages = JSON.parse(q.images);
      } catch (e) {
        parsedImages = null;
      }
    }
    
    // Ensure images is an array or null
    if (parsedImages && !Array.isArray(parsedImages)) {
      parsedImages = null;
    }
    
    return {
      ...q,
      options: parsedOptions,
      images: parsedImages
    };
  });
  
  const [questions, setQuestions] = useState(initialQuestions);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});

  // Debug: Log questions with images
  useEffect(() => {
    console.log('=== Question Bank Edit Page ===');
    console.log('Total questions:', questions.length);
    questions.forEach((q, idx) => {
      console.log(`Question ${idx + 1}:`, {
        id: q.id,
        text: q.text?.substring(0, 50) + '...',
        hasImages: !!q.images,
        imagesType: typeof q.images,
        imagesCount: Array.isArray(q.images) ? q.images.length : 0,
        images: q.images
      });
    });
  }, [questions]);

  useEffect(() => {
    if (!group) {
      navigate(-1); // Go back if no group data
    }
  }, [group, navigate]);

  const handleQuestionChange = (questionId, field, value) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q =>
        q.id === questionId ? { ...q, [field]: value } : q
      )
    );
  };

  const handleOptionChange = (questionId, optionIndex, value) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id === questionId) {
          const newOptions = [...(Array.isArray(q.options) ? q.options : [])];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const addOption = (questionId) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id === questionId) {
          const currentOptions = Array.isArray(q.options) ? q.options : [];
          return { ...q, options: [...currentOptions, ''] };
        }
        return q;
      })
    );
  };

  const removeOption = (questionId, optionIndex) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id === questionId) {
          const currentOptions = Array.isArray(q.options) ? q.options : [];
          if (currentOptions.length > 2) {
            const newOptions = currentOptions.filter((_, idx) => idx !== optionIndex);
            return { ...q, options: newOptions };
          }
        }
        return q;
      })
    );
  };

  const handleSaveQuestion = async (questionId) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    // Validate that an answer is selected
    if (!question.answer || question.answer === '') {
      alert('⚠️ Please select a correct answer before saving!');
      return;
    }

    setSaveStatus(prev => ({ ...prev, [questionId]: 'saving' }));

    try {
      const payload = {
        text: question.text,
        options: JSON.stringify(question.options),
        answer: question.answer,
        year: question.year,
        subject: question.subject,
        section: question.section,
        topic: question.topic,
        images: question.images || null
      };

      console.log('Saving question:', questionId, 'with answer:', question.answer);
      console.log('Full payload:', payload);

      await api.put(`/questions/${questionId}`, payload);
      setSaveStatus(prev => ({ ...prev, [questionId]: 'success' }));
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [questionId]: null }));
      }, 2000);
    } catch (error) {
      console.error('Error updating question:', error);
      setSaveStatus(prev => ({ ...prev, [questionId]: 'error' }));
      alert('❌ Error saving question: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const promises = questions.map(question => {
        const payload = {
          text: question.text,
          options: JSON.stringify(question.options),
          answer: question.answer,
          year: question.year,
          subject: question.subject,
          section: question.section,
          topic: question.topic,
          images: question.images || null
        };
        return api.put(`/questions/${question.id}`, payload);
      });

      await Promise.all(promises);
      alert('✅ All questions saved successfully!');
      navigate(-1); // Go back to question bank
    } catch (error) {
      console.error('Error saving all questions:', error);
      alert('❌ Error saving questions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!group) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 border-indigo-600">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                📝 Edit Question Bank
              </h1>
              <p className="text-lg text-gray-600">
                <span className="font-semibold text-indigo-600">Year {group.year}</span>
                {' • '}
                <span className="font-semibold text-purple-600">{group.subject}</span>
                {' • '}
                <span className="font-semibold text-pink-600">Section {group.section}</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {questions.length} question{questions.length !== 1 ? 's' : ''} in this group
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium transition-colors shadow-md"
              >
                ← Back
              </button>
              <button
                onClick={handleSaveAll}
                disabled={saving}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium transition-colors shadow-md disabled:opacity-50"
              >
                {saving ? '💾 Saving All...' : '💾 Save All Questions'}
              </button>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-6">
          {questions.map((question, qIndex) => {
            const questionOptions = Array.isArray(question.options) ? question.options : [];
            const correctAnswer = question.answer;
            const status = saveStatus[question.id];

            return (
              <div key={question.id} className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
                {/* Question Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
                  <div>
                    <h5 className="font-bold text-xl">Question {qIndex + 1}</h5>
                    <p className="text-sm text-indigo-100">ID: {question.id}</p>
                  </div>
                  {status === 'success' && (
                    <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-pulse">
                      ✓ Saved!
                    </span>
                  )}
                  {status === 'error' && (
                    <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                      ✗ Error
                    </span>
                  )}
                </div>

                <div className="p-6 space-y-5">
                  {/* Question Text */}
                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-indigo-500">
                    <label htmlFor={`question-text-${question.id}`} className="block text-sm font-semibold text-gray-700 mb-2">
                      📖 Question Text
                    </label>
                    <textarea
                      id={`question-text-${question.id}`}
                      value={question.text || ''}
                      onChange={(e) => handleQuestionChange(question.id, 'text', e.target.value)}
                      rows={5}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium text-gray-800 resize-none"
                      placeholder="Enter question text..."
                    />
                    </div>

                    {/* Question Images (if exists) */}
                    {question.images && Array.isArray(question.images) && question.images.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
                        <p className="text-sm font-semibold text-gray-700 mb-3">🖼️ Question Images ({question.images.length})</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {question.images.map((imageUrl, imgIndex) => (
                            <div key={imgIndex} className="relative">
                              <img 
                                src={imageUrl} 
                                alt={`Question ${qIndex + 1} attachment ${imgIndex + 1}`}
                                className="w-full h-auto rounded-lg shadow-md border border-gray-200"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  console.error('Failed to load image:', imageUrl);
                                }}
                              />
                              <span className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                                Image {imgIndex + 1}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Options */}
                  <div className="bg-green-50 rounded-lg p-5 border-l-4 border-green-500">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-semibold text-gray-700">📋 Answer Options</div>
                      <button
                        onClick={() => addOption(question.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium shadow-sm transition-colors"
                      >
                        ➕ Add Option
                      </button>
                    </div>
                    <div className="space-y-3">
                      {questionOptions.map((option, optIndex) => {
                        const optionLetter = String.fromCharCode(65 + optIndex);
                        const isCorrect = option === correctAnswer;

                        return (
                          <div key={optIndex} className={`flex gap-3 items-center p-4 rounded-lg border-2 transition-all ${
                            isCorrect 
                              ? 'bg-green-100 border-green-500 shadow-md' 
                              : 'bg-white border-gray-300'
                          }`}>
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-base ${
                              isCorrect 
                                ? 'bg-green-500 text-white shadow-md' 
                                : 'bg-gray-200 text-gray-700'
                            }`}>
                              {optionLetter}
                            </div>
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                              placeholder={`Option ${optionLetter}`}
                              className={`flex-1 px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base ${
                                isCorrect 
                                  ? 'border-green-400 bg-white font-semibold text-gray-900' 
                                  : 'border-gray-300'
                              }`}
                            />
                            {isCorrect && (
                              <span className="flex-shrink-0 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md">
                                ✓ CORRECT
                              </span>
                            )}
                            {questionOptions.length > 2 && (
                              <button
                                onClick={() => removeOption(question.id, optIndex)}
                                className="flex-shrink-0 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium shadow-sm transition-colors"
                              >
                                🗑️ Remove
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Correct Answer Selector */}
                  <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-500">
                    <label htmlFor={`answer-${question.id}`} className="block text-sm font-semibold text-gray-700 mb-2">
                      ✅ Select Correct Answer
                      {correctAnswer && (
                        <span className="ml-2 text-green-600 font-bold">
                          (Currently: {questionOptions.findIndex(opt => opt === correctAnswer) !== -1 
                            ? String.fromCharCode(65 + questionOptions.findIndex(opt => opt === correctAnswer)) 
                            : 'Not in options'})
                        </span>
                      )}
                    </label>
                    <select
                      id={`answer-${question.id}`}
                      value={correctAnswer || ''}
                      onChange={(e) => {
                        console.log('Answer changed for question', question.id, 'to:', e.target.value);
                        handleQuestionChange(question.id, 'answer', e.target.value);
                      }}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 font-medium text-gray-800 text-base ${
                        correctAnswer ? 'border-green-500 bg-green-50' : 'border-gray-300'
                      }`}
                    >
                      <option value="">-- Select the correct answer --</option>
                      {questionOptions.map((opt, i) => (
                        <option key={i} value={opt}>
                          {String.fromCharCode(65 + i)}) {opt}
                        </option>
                      ))}
                    </select>
                    {!correctAnswer && (
                      <p className="mt-2 text-sm text-red-600 font-semibold">
                        ⚠️ No answer selected! Please select the correct answer before saving.
                      </p>
                    )}
                  </div>

                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-4 bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500">
                    <div>
                      <label htmlFor={`year-${question.id}`} className="block text-sm font-semibold text-gray-700 mb-2">
                        📅 Year
                      </label>
                      <input
                        id={`year-${question.id}`}
                        type="text"
                        value={question.year || ''}
                        onChange={(e) => handleQuestionChange(question.id, 'year', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., 2024"
                      />
                    </div>
                    <div>
                      <label htmlFor={`subject-${question.id}`} className="block text-sm font-semibold text-gray-700 mb-2">
                        📚 Subject
                      </label>
                      <input
                        id={`subject-${question.id}`}
                        type="text"
                        value={question.subject || ''}
                        onChange={(e) => handleQuestionChange(question.id, 'subject', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., Mathematics"
                      />
                    </div>
                    <div>
                      <label htmlFor={`section-${question.id}`} className="block text-sm font-semibold text-gray-700 mb-2">
                        📋 Section
                      </label>
                      <input
                        id={`section-${question.id}`}
                        type="text"
                        value={question.section || ''}
                        onChange={(e) => handleQuestionChange(question.id, 'section', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., A"
                      />
                    </div>
                    <div>
                      <label htmlFor={`topic-${question.id}`} className="block text-sm font-semibold text-gray-700 mb-2">
                        🏷️ Topic
                      </label>
                      <input
                        id={`topic-${question.id}`}
                        type="text"
                        value={question.topic || ''}
                        onChange={(e) => handleQuestionChange(question.id, 'topic', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., Algebra"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={() => handleSaveQuestion(question.id)}
                    disabled={status === 'saving'}
                    className="w-full px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.01] disabled:opacity-50"
                  >
                    {status === 'saving' ? '💾 Saving...' : `💾 Save Question ${qIndex + 1}`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Actions */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6 flex justify-between items-center">
          <button
            onClick={() => navigate(-1)}
            className="px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium transition-colors shadow-md"
          >
            ← Cancel & Go Back
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {saving ? '💾 Saving All Questions...' : '💾 Save All & Return to Bank'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionBankEditPage;
